import { useEffect, useState } from 'react';
import { getDeferredPrompt, clearDeferredPrompt, subscribeInstall } from '../../pwaInstall';
import './InstallPrompt.css';

const DISMISS_KEY = 'toolsight_pwa_install_dismissed';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as "Macintosh"; disambiguate via touch support.
  const iPadOS = ua.includes('Macintosh') && 'ontouchend' in document;
  return iOSDevice || iPadOS;
}

function wasDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismiss() {
  try {
    window.localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* storage unavailable — banner may reappear next launch; acceptable */
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredPrompt());
  const [dismissed, setDismissed] = useState(() => wasDismissed());
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const unsub = subscribeInstall((evt) => {
      setDeferredPrompt(evt);
      if (evt === null) setInstalled(isStandalone());
    });
    return unsub;
  }, []);

  if (installed || dismissed) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* no-op */
    }
    clearDeferredPrompt();
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    persistDismiss();
    setDismissed(true);
  };

  // Android / any beforeinstallprompt-capable browser: real install button.
  if (deferredPrompt) {
    return (
      <div className="tsinstall-banner" role="region" aria-label="Install ToolSight">
        <div className="tsinstall-text">
          <span className="tsinstall-title">Install ToolSight</span>
          <span className="tsinstall-sub">Add it to your home screen for one-tap access.</span>
        </div>
        <div className="tsinstall-actions">
          <button type="button" className="tsinstall-install" onClick={handleInstall}>
            Install
          </button>
          <button
            type="button"
            className="tsinstall-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari: no install API — show Add-to-Home-Screen instructions.
  if (isIOS()) {
    return (
      <div className="tsinstall-banner" role="region" aria-label="Install ToolSight">
        <div className="tsinstall-text">
          <span className="tsinstall-title">Install ToolSight</span>
          <span className="tsinstall-sub">Tap the Share button, then “Add to Home Screen.”</span>
        </div>
        <div className="tsinstall-actions">
          <button
            type="button"
            className="tsinstall-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  // Desktop, unsupported browsers, or criteria not yet met → nothing.
  return null;
}
